import type React from "react";
import { cn } from "../../lib/utils";

type SettingsSectionProps = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  action?: React.ReactNode;
  danger?: boolean;
  className?: string;
};

export function SettingsSection({
  title,
  icon: Icon,
  children,
  action,
  danger,
  className,
}: SettingsSectionProps) {
  return (
    <div
      className={cn(
        "relative rounded-xl border overflow-hidden",
        danger
          ? "border-error-500/20 bg-error-500/5"
          : "border-surface-700/50 bg-gradient-to-b from-surface-800/70 via-surface-850/50 to-surface-800/40",
        className,
      )}
    >
      {/* Top accent line (danger only) */}
      {danger && (
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-error-500/30 to-transparent" />
      )}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Header */}
      <div
        className={cn(
          "relative flex items-center justify-between px-6 pt-5 pb-4 border-b",
          danger ? "border-error-500/15" : "border-surface-700/40",
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex items-center justify-center size-9 rounded-lg border",
              danger
                ? "bg-error-500/10 border-error-500/15"
                : "bg-brand-500/10 border-brand-500/15",
            )}
          >
            <Icon className={cn("size-4.5", danger ? "text-error-400" : "text-brand-400")} />
          </div>
          <h2
            className={cn(
              "font-display text-lg font-bold tracking-wide",
              danger ? "text-error-400" : "text-white",
            )}
          >
            {title}
          </h2>
        </div>
        {action}
      </div>

      {/* Body */}
      <div className="relative p-6">{children}</div>
    </div>
  );
}
